import { AgentStatus } from '@entities/agent-status';
import { IntegrationSettingsValueType } from '@entities/integration';
import type { UserInfo, UserPhoneNumber } from '@entities/user';
import { transformCrmStatusId } from '@infra/repositories/integration-mem.repository';
import type { BusUserAvailabilityChangedPayload } from '@main/event-listeners/payloads/bus-user-availability-changed.payload';
import { UserPresenceStatusStore } from '@main/sf-connector/user-presence-status-store';
import { Logger } from '@main/utils/logger';
import type { BaseUsecase } from '@usecases/base-usecase';
import type { Call } from '../entities/call';
import { CallStatuses, CallTypes } from '../entities/call';

/**
 * TODO: Refine this request object base on Agent status option we have for Phone.
 */
export type StatusSyncAircallToCrmRequest = BusUserAvailabilityChangedPayload;

export type StatusSyncAircallToCrmUsecase = BaseUsecase<
  StatusSyncAircallToCrmRequest,
  void
>;

export interface StatusSyncAircallToCrmEventPublisher {
  publishStatus(status: string): Promise<void>;
}

export interface StatusSyncAircallToCrmUserRepository {
  getCRMPreviousStatus(): Promise<string | undefined>;
  setCRMPreviousStatus(status: string): Promise<void>;
  getCurrentCRMStatus(): Promise<string | void>;
  setCurrentCRMStatus(status: string): Promise<void>;
  setPhoneStatus(status: AgentStatus): Promise<void>;
  cleanPreviousCRMStatus(): Promise<void>;
  getPhoneNumberByNormalizedNumber(
    normalized: string,
  ): Promise<UserPhoneNumber>;
  getUserInfo(): UserInfo | undefined;
}

export interface StatusSyncAircallToCrmCallRepository {
  getCurrentCall(): Promise<Call>;
}

export interface StatusSyncAircallToCRMSettingsRepository {
  getAircallToCRMStatusIdMappingByPhoneNumber(
    numberId: number,
  ): Promise<string>;
}

export class StatusSyncAircallToCrm implements StatusSyncAircallToCrmUsecase {
  private logger: Logger;
  constructor(
    private userRepository: StatusSyncAircallToCrmUserRepository,
    private callRepository: StatusSyncAircallToCrmCallRepository,
    private integrationRepository: StatusSyncAircallToCRMSettingsRepository,
    private publisher: StatusSyncAircallToCrmEventPublisher,
  ) {
    this.logger = new Logger();
  }

  async run(status: StatusSyncAircallToCrmRequest): Promise<void> {
    // check if the user is the same as the current user
    const currentUser = this.userRepository.getUserInfo();
    if (currentUser?.id !== status.userId) {
      return;
    }

    //get mapped agent status
    const agentStatus = this.getAgentStatusByPhoneStatus(status.status);
    await this.userRepository.setPhoneStatus(agentStatus);

    const currentCall = await this.callRepository.getCurrentCall();

    if (currentCall && currentCall.status !== CallStatuses.ENDED) {
      await this.updateStatusByIntegrationSetting(currentCall);
    } else {
      await this.revertCRMStatus();
    }
  }

  private async revertCRMStatus() {
    const previousStatus = await this.userRepository.getCRMPreviousStatus();
    if (!previousStatus) return;

    const currentStatus = await this.userRepository.getCurrentCRMStatus();
    if (previousStatus !== currentStatus) {
      await this.userRepository.setCurrentCRMStatus(previousStatus);
      await this.publisher.publishStatus(previousStatus);
    }
    await this.userRepository.cleanPreviousCRMStatus();
  }

  private async updateStatusByIntegrationSetting(currentCall: Call) {
    const currentCRMStatus = await this.userRepository.getCurrentCRMStatus();

    // Do not update status if CRM status is not available or it is offline
    if (
      !currentCRMStatus ||
      currentCRMStatus === UserPresenceStatusStore.getOfflineStatusId()
    ) {
      return;
    }

    const phoneNumberForSetting =
      currentCall.type === CallTypes.INBOUND
        ? currentCall.to
        : currentCall.from;

    const { id: numberId } =
      await this.userRepository.getPhoneNumberByNormalizedNumber(
        phoneNumberForSetting,
      );

    const crmStatusIdToUpdate = numberId
      ? await this.integrationRepository.getAircallToCRMStatusIdMappingByPhoneNumber(
          numberId,
        )
      : '';

    if (
      !crmStatusIdToUpdate ||
      crmStatusIdToUpdate === IntegrationSettingsValueType.NOTHING
    ) {
      return;
    }

    // Update previous status with current status so that it can be reverted back when call ends
    await this.userRepository.setCRMPreviousStatus(currentCRMStatus);

    if (crmStatusIdToUpdate !== currentCRMStatus) {
      /*
       * We don't really know why, but SF OmniChannel JS SDK only works if we cut
       * the last three characters from the Status ID
       */
      const updatedStatus = transformCrmStatusId(crmStatusIdToUpdate);
      await this.userRepository.setCurrentCRMStatus(updatedStatus);
      await this.publisher.publishStatus(updatedStatus);
    }
  }

  private getAgentStatusByPhoneStatus(status: string): AgentStatus {
    switch (status) {
      case 'available':
        return AgentStatus.AVAILABLE;
      case 'offline':
      case 'do_not_disturb':
        return AgentStatus.UNAVAILABLE;
      default:
        return AgentStatus.OTHER;
    }
  }
}
